import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';

const CancelacionModal = ({
  open,
  onClose,
  onSubmit,
  onConfirm,
  observaciones,
  onObservacionesChange,
  folio,
  solicitudData,
}) => {
  const [error, setError] = useState('');
  const [localObservaciones, setLocalObservaciones] = useState('');

  const isControlled = typeof onObservacionesChange === 'function';
  const currentObservaciones = isControlled ? observaciones : localObservaciones;
  const currentFolio = folio || solicitudData?.folio || '';
  const submitHandler = useMemo(() => onConfirm || onSubmit, [onConfirm, onSubmit]);

  useEffect(() => {
    if (open) {
      setLocalObservaciones(solicitudData?.observaciones || '');
      setError('');
    }
  }, [open, solicitudData?.observaciones]);

  const validate = () => {
    const value = (currentObservaciones || '').trim();
    if (!value) return 'Debes escribir el motivo del rechazo.';
    if (value.length < 5) return 'Escribe un motivo más detallado (mín. 5 caracteres).';
    return '';
  };

  const handleEnviar = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');

    submitHandler?.({
      motivo: 'Cancelacion',
      detalle: currentObservaciones,
      secciones: 'TODAS',
      reclasificar: 0,
    });
  };

  const handleChangeDetalle = (e) => {
    if (isControlled) {
      onObservacionesChange?.(e);
    } else {
      setLocalObservaciones(e.target.value);
    }

    if (error) setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25)',
          background: '#ffffff',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 'clamp(12px, 1.5vw, 18px)',
          py: 'clamp(10px, 1.2vw, 14px)',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(8px, 1vw, 12px)',
          background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.18)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
          }}
        >
          <DoDisturbOffIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#ffffff' }} />
        </Paper>
        <Typography sx={{ fontWeight: 800, flex: 1, fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1rem)', lineHeight: 1.2 }}>
          Confirmar Rechazo
        </Typography>
        <Tooltip title="Cerrar">
          <IconButton onClick={onClose} sx={{ color: '#ffffff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)' }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 'clamp(14px, 2vw, 20px)', bgcolor: '#ffffff' }}>
        <DialogContentText sx={{ mb: 'clamp(10px, 1.5vw, 16px)', fontSize: 'clamp(0.74rem, 0.70rem + 0.1vw, 0.82rem)', color: '#475569', lineHeight: 1.4 }}>
          Indica la razón por la que se rechaza la solicitud {currentFolio ? <b>#{currentFolio}</b> : ''}:
        </DialogContentText>

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          placeholder="Ej: El monto facturado no coincide con la orden de compra..."
          value={currentObservaciones || ''}
          onChange={handleChangeDetalle}
          error={Boolean(error)}
          helperText={error}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              fontSize: 'clamp(0.72rem, 0.68rem + 0.08vw, 0.80rem)',
              p: 'clamp(8px, 1vw, 12px)',
              '& fieldset': { borderColor: error ? '#ef4444' : '#cbd5e1' },
              '&:hover fieldset': { borderColor: error ? '#dc2626' : '#94a3b8' },
              '&.Mui-focused fieldset': { borderColor: error ? '#dc2626' : '#0655A5' },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 'clamp(12px, 1.5vw, 18px)', py: 'clamp(8px, 1.2vw, 12px)', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 0.8 }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{
            borderRadius: '8px',
            color: '#64748b',
            fontWeight: 600,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.76rem)',
            textTransform: 'none',
            px: 'clamp(10px, 1.2vw, 14px)',
            height: 'clamp(26px, 2vw, 30px)',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          Volver
        </Button>
        <Button
          onClick={handleEnviar}
          variant="contained"
          size="small"
          color="error"
          sx={{
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.76rem)',
            bgcolor: '#dc2626',
            boxShadow: 'none',
            textTransform: 'none',
            px: 'clamp(12px, 1.5vw, 18px)',
            height: 'clamp(26px, 2vw, 30px)',
            '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
          }}
        >
          Confirmar Rechazo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelacionModal;
